import { useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Upload, X, FileText, Image, AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest, secureFetch } from "@/lib/queryClient";
import { captureEvent } from "@/lib/posthog";

const FALLBACK_MAX_FILES = 50;
const FALLBACK_MAX_MB = 100;
const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg", "image/jpg", "image/webp"];
const ALLOWED_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg", ".webp"];

interface UploadFile {
  file: File;
  id: string;
  error?: string;
}

interface AIUploadWizardProps {
  open: boolean;
  onClose: () => void;
  creatorOrgId: string | null;
}

interface OrgLimits {
  maxFiles: number;
  maxTotalMb: number;
  pilotEnabled: boolean;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(file: File) {
  if (file.type === "application/pdf") return <FileText className="h-4 w-4 text-red-500" />;
  return <Image className="h-4 w-4 text-blue-500" />;
}

export function AIUploadWizard({ open, onClose, creatorOrgId }: AIUploadWizardProps) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const { data: limitsData, isLoading: limitsLoading } = useQuery<OrgLimits>({
    queryKey: ["/api/creator/ai-upload/limits", creatorOrgId],
    queryFn: () =>
      apiRequest("GET", `/api/creator/ai-upload/limits${creatorOrgId ? `?orgId=${creatorOrgId}` : ""}`).then(
        (r) => r.json()
      ),
    enabled: open,
    staleTime: 5 * 60_000,
  });

  const maxFiles = limitsData?.maxFiles ?? FALLBACK_MAX_FILES;
  const maxTotalBytes = (limitsData?.maxTotalMb ?? FALLBACK_MAX_MB) * 1024 * 1024;

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!creatorOrgId) throw new Error("No org context");
      const formData = new FormData();
      formData.append("orgId", creatorOrgId);
      files.forEach((f) => formData.append("files", f.file));

      const res = await secureFetch("/api/creator/ai-upload/jobs", {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Upload failed");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      captureEvent("ai_upload_job_created", {
        jobId: data?.jobId,
        orgId: creatorOrgId,
        fileCount: files.length,
        totalSizeBytes: files.reduce((acc, f) => acc + f.file.size, 0),
      });
      toast({ title: "Processing started", description: "Your files are being analysed. Check the job list below for status updates." });
      queryClient.invalidateQueries({ queryKey: ["/api/creator/ai-upload/jobs"] });
      handleClose();
    },
    onError: (err: Error) => {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });

  function handleClose() {
    setFiles([]);
    onClose();
  }

  function validateAndAddFiles(incoming: File[]) {
    const updated = [...files];
    for (const file of incoming) {
      if (updated.length >= maxFiles) {
        toast({
          title: "Too many files",
          description: `Max ${maxFiles} files per upload.`,
          variant: "destructive",
        });
        break;
      }
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: "Unsupported file type",
          description: `${file.name} — only PDF and images are allowed.`,
          variant: "destructive",
        });
        continue;
      }
      const totalBytes = updated.reduce((acc, f) => acc + f.file.size, 0) + file.size;
      if (totalBytes > maxTotalBytes) {
        toast({
          title: "Upload too large",
          description: `Adding ${file.name} would exceed the ${limitsData?.maxTotalMb ?? FALLBACK_MAX_MB} MB total limit.`,
          variant: "destructive",
        });
        break;
      }
      updated.push({ file, id: `${file.name}-${Date.now()}-${Math.random()}` });
    }
    setFiles(updated);
  }

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      validateAndAddFiles(Array.from(e.dataTransfer.files));
    },
    [files, maxFiles, maxTotalBytes]
  );

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) validateAndAddFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const removeFile = (id: string) => setFiles((prev) => prev.filter((f) => f.id !== id));

  const totalBytes = files.reduce((acc, f) => acc + f.file.size, 0);
  const canSubmit = files.length > 0 && totalBytes <= maxTotalBytes;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="ai-upload-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Bulk AI-Assisted Upload
          </DialogTitle>
          <DialogDescription>
            Upload your PDFs or images. We'll extract the content and build a structured draft for you.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30"
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="dropzone-ai-upload"
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_EXTENSIONS.join(",")}
              className="hidden"
              onChange={handleFileInput}
              data-testid="input-ai-upload-files"
            />
            <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-foreground font-medium">Drop files here or click to browse</p>
            {limitsLoading ? (
              <p className="text-xs text-muted-foreground mt-1 flex items-center justify-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading your upload limits…
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                PDF and images only · Up to {maxFiles} files · {limitsData?.maxTotalMb ?? FALLBACK_MAX_MB} MB total
              </p>
            )}
          </div>

          {files.length > 0 && (
            <div className="space-y-2" data-testid="file-list">
              {files.map((f) => (
                <div
                  key={f.id}
                  className="flex items-center gap-2 p-2 rounded-md border border-border bg-muted/20"
                  data-testid={`file-item-${f.id}`}
                >
                  {fileIcon(f.file)}
                  <span className="flex-1 text-xs truncate text-foreground">{f.file.name}</span>
                  <span className="text-xs text-muted-foreground flex-shrink-0">{formatBytes(f.file.size)}</span>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    data-testid={`button-remove-file-${f.id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
                <span>{files.length}/{maxFiles} files</span>
                <span className={totalBytes > maxTotalBytes ? "text-destructive font-medium" : ""}>
                  {formatBytes(totalBytes)} / {limitsData?.maxTotalMb ?? FALLBACK_MAX_MB} MB
                </span>
              </div>
            </div>
          )}

          {totalBytes > maxTotalBytes && (
            <div className="flex items-center gap-2 text-destructive text-xs p-2 rounded bg-destructive/10">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              Total size exceeds {limitsData?.maxTotalMb ?? FALLBACK_MAX_MB} MB. Remove some files.
            </div>
          )}

          <div className="flex gap-2 pt-1">
            <Button variant="outline" onClick={handleClose} data-testid="button-wizard-cancel">
              Cancel
            </Button>
            <Button
              className="flex-1"
              disabled={!canSubmit || submitMutation.isPending}
              onClick={() => submitMutation.mutate()}
              data-testid="button-wizard-submit"
            >
              {submitMutation.isPending ? "Starting…" : "Start processing"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
